import type { AppointmentStatus } from '../../appointments/domain/types';

/** Statuses that count toward k in k/N (parity with portal). */
export const COUNTED_SESSION_STATUSES = new Set<AppointmentStatus | string>([
  'requested',
  'confirmed',
  'completed',
]);

export type QuoteApptSessionRef = {
  sessionIndex: number | null;
  status: AppointmentStatus | string;
};

export type SessionProgress = {
  total: number;
  bookedCount: number;
  /** Distinct session indices that count toward progress. */
  bookedIndexes: number[];
};

/** Unique session slots with an active/completed booking (excludes evaluation; eval has no quote_request_id). */
export function computeSessionProgress(
  appointments: QuoteApptSessionRef[],
  estimatedSessions: number,
): SessionProgress {
  const total = Math.max(1, Number(estimatedSessions) || 1);
  const indexes = new Set<number>();
  for (const a of appointments) {
    if (a.sessionIndex == null) continue;
    if (!COUNTED_SESSION_STATUSES.has(a.status)) continue;
    indexes.add(a.sessionIndex);
  }
  const bookedIndexes = [...indexes].sort((a, b) => a - b);
  return { total, bookedCount: bookedIndexes.length, bookedIndexes };
}

/** List / board / sheet label. Pass `total` when cheap; omit if unknown. */
export function formatQuoteSessionMeta(
  sessionIndex: number | null | undefined,
  total?: number | null,
): string {
  if (sessionIndex == null) return 'Cotización';
  if (total != null && total > 0) return `Cotización · Sesión ${sessionIndex}/${total}`;
  return `Cotización · Sesión ${sessionIndex}`;
}

/** First free slot in 1..total, or null when plan is fully booked. */
export function nextSessionIndex(progress: SessionProgress): number | null {
  if (progress.bookedCount >= progress.total) return null;
  for (let i = 1; i <= progress.total; i++) {
    if (!progress.bookedIndexes.includes(i)) return i;
  }
  return null;
}

/** Store CTA — parity with portal tone (es-AR). */
export function formatScheduleSessionCta(progress: SessionProgress): string {
  const next = nextSessionIndex(progress);
  if (next == null || progress.total <= 1) return 'Agendar sesión';
  return `Agendar sesión ${next} de ${progress.total}`;
}
